import React, { useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { Box, Paper, Typography, TextField, IconButton, Tooltip, useTheme } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import JsonView from '@uiw/react-json-view';
import { vscodeTheme } from '@uiw/react-json-view/vscode';
import SEO, { createSoftwareAppSchema } from '../../components/SEO';
import ToolPageContainer from '../../components/ToolPageContainer';

const JsonPanel = ({ title, data, onCopy }) => {
  const theme = useTheme();
  const { mode } = theme.palette;

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6">{title}</Typography>
        <Tooltip title="Copy">
          <IconButton onClick={onCopy} size="small" disabled={!data}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Paper variant="outlined" sx={{ flexGrow: 1, p: 2, overflow: 'auto' }}>
        {data ? (
          <JsonView value={data} style={mode === 'dark' ? vscodeTheme.dark : vscodeTheme.light} />
        ) : (
          <Typography color="text.secondary">...</Typography>
        )}
      </Paper>
    </Box>
  );
};

function JWTDecoder() {
  const [token, setToken] = useState('eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c');
  const [decoded, setDecoded] = useState({ header: null, payload: null, signature: '' });
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token.trim()) {
      setDecoded({ header: null, payload: null, signature: '' });
      setError('');
      return;
    }

    try {
      const header = jwtDecode(token, { header: true });
      const payload = jwtDecode(token);
      const signature = token.split('.')[2] || '';
      setDecoded({ header, payload, signature });
      setError('');
    } catch (e) {
      setDecoded({ header: null, payload: null, signature: '' });
      setError('Invalid JWT Token');
    }
  }, [token]);

  const handleCopy = (data) => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
  };

  return (
    <>
      <SEO
        title="JWT Decoder | JSON Web Token Inspector | UmbrellaCo"
        description="Decode and inspect JSON Web Tokens (JWT) securely in your browser. View header, payload claims, and signature details for free online."
        keywords="jwt decoder, json web token, jwt parser, decode jwt online, jwt inspector"
        url="/dev-tools/jwt-decoder"
        jsonLd={createSoftwareAppSchema(
          'JWT Decoder',
          'Decode and inspect JSON Web Tokens securely in your browser.',
          'https://umbrellaco.in/dev-tools/jwt-decoder',
          ['JWT Parsing', 'Security Tools', 'Developer Utilities']
        )}
      />
      <ToolPageContainer
        title="JWT Inspector"
        description="Paste a JSON Web Token to securely decode its header, payload claims, and signature entirely within your browser."
      >

      <Box sx={{ mb: 2 }}>
        <TextField
          label="JSON Web Token"
          multiline
          rows={4}
          value={token}
          onChange={(e) => setToken(e.target.value)}
          variant="outlined"
          fullWidth
          error={!!error}
          helperText={error}
        />
      </Box>

      <Box sx={{ flexGrow: 1, display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
        <JsonPanel title="Header" data={decoded.header} onCopy={() => handleCopy(decoded.header)} />
        <JsonPanel title="Payload" data={decoded.payload} onCopy={() => handleCopy(decoded.payload)} />
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Signature</Typography>
          <Paper variant="outlined" sx={{ flexGrow: 1, p: 2, overflow: 'auto', wordBreak: 'break-all', fontFamily: 'monospace' }}>
            {decoded.signature || '...'}
          </Paper>
        </Box>
      </Box>
      </ToolPageContainer>
    </>
  );
}

export default JWTDecoder;
